<template>
    <div class="job_wrapper">
        <div class="modal_title text_center">
            {{ labels.name || defaultLabels.name }}
        </div>
        <div class="form_title second_font">
            {{ labels.subname || defaultLabels.subname }}
        </div>
        <div ref="form" class="rows_wrapper">
            <div class="form_row">
                <label for="title" class="label_title">
                    {{ labels.title || defaultLabels.title }}
                </label>
                <input
                    id="title"
                    v-model="post.title"
                    type="text"
                    class="form_control"
                    style="width: 100%"
                    placeholder="כותרת"
                    :class="{ 'is-invalid': hasError('title') }"
                    @input="clearServerError('title')"
                />
                <div class="invalid-feedback">
                    {{ getError('title') }}
                </div>
            </div>

            <div class="form_row">
                <label for="subtitle" class="label_title">
                    {{ labels.subtitle || defaultLabels.subtitle }}
                </label>
                <input
                    id="subtitle"
                    v-model="post.subtitle"
                    type="text"
                    class="form_control"
                    style="width: 100%"
                    placeholder="כותרת משנה"
                    :class="{ 'is-invalid': hasError('subtitle') }"
                    @input="clearServerError('subtitle')"
                />
                <div class="invalid-feedback">
                    {{ getError('subtitle') }}
                </div>
            </div>

            <div class="form_row">
                <label for="description" class="label_title">
                    {{ labels.description || defaultLabels.description }}
                </label>

                <textarea
                    id="description"
                    v-model="post.description"
                    type="description"
                    class="form_control"
                    style="width: 100%"
                    placeholder="תיאור"
                    rows="5"
                    :class="{ 'is-invalid': hasError('description') }"
                    @input="clearServerError('description')"
                />

                <div class="invalid-feedback">
                    {{ getError('description') }}
                </div>
            </div>

            <div class="form_row">
                <label class="label_title" for="featuredImage">
                    {{ labels.featuredImage || defaultLabels.featuredImage }}
                </label>

                <div
                    class="file_upload_block"
                    :class="{
                        'is-invalid': hasError('featuredImage')
                    }"
                    style="margin-top: 0;"
                >
                    <div class="icon_wrapper">
                        <picture v-full-image-path:assets="'file_icon.svg'" />
                    </div>

                    <div class="text_center">
                        <label class="file_btn file_upload_button">
                            <input
                                id="featuredImage"
                                ref="fileInput"
                                accept="image/*"
                                type="file"
                                style="width: 100%"
                                @change="changeFile"
                            />

                            <span>בחר/י קובץ</span>
                        </label>

                        <div class="file_info">
                            IMAGE
                        </div>
                    </div>

                    <div
                        v-if="post.featuredImage"
                        class="upload_file_wrapper text_center"
                    >
                        <div class="file_row">
                            <div class="upload_file">
                                נבחר :
                                <span class="file_name">
                                    {{ post.featuredImage.name }}
                                </span>

                                <button
                                    class="btn_remove_file"
                                    @click="removeFile"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="invalid-feedback">
                    {{ getError('featuredImage') }}
                </div>
            </div>

            <question
                v-for="(question, index) in questions"
                :key="question.id"
                :question="question"
                :validation="$v.questions.$each[index].answer"
                :server-errors="serverErrors"
                @input="onInput($event, index)"
            />
        </div>

        <div class="checkbox_wrapper">
            <label class="label_checkbox">
                <input v-model="sitesPolicy" type="checkbox" />
                <span
                    class="label_checkbox_content"
                    :class="{
                        'is-invalid': $v.sitesPolicy.$error
                    }"
                >
                    אני מאשר/ת שקראתי את
                    <nuxt-link to="/terms-of-use" target="_blank">
                        תקנון האתר
                    </nuxt-link>
                </span>
            </label>
        </div>

        <div class="btn_wrapper text_left">
            <button class="btn btn_green" type="button" @click="save">
                העלאה
            </button>
        </div>

        <recaptcha-message />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { email, required } from 'vuelidate/lib/validators';
import { StatusCodes as HTTP } from 'http-status-codes';
import Question from '@/components/boards/Question';
import recaptchaMixin from '@/mixins/recaptcha.mixin.js';

export default {
    components: {
        Question
    },

    mixins: [recaptchaMixin],

    props: {
        boardId: {
            type: String,
            required: true
        }
    },

    validations: {
        post: {
            title: {
                required
            },
            subtitle: {
                required
            },
            description: {
                required
            },
            featuredImage: {
                isFormatValid(file) {
                    if (!file) {
                        return true;
                    }

                    const [type] = file.type.split('/');

                    return type === 'image';
                }
            }
        },
        questions: {
            $each: {
                answer: {
                    required(val, { isRequired }) {
                        if (!isRequired) {
                            return true;
                        }

                        return !!val;
                    },
                    isFormatValid(val, { regex: pattern }) {
                        if (!pattern || !val) {
                            return true;
                        }

                        const regex = new RegExp(pattern);

                        return regex.test(val);
                    },
                    email(val, { type }) {
                        if (type === 'email') {
                            return email(val);
                        }

                        return true;
                    },
                    correctQuantity(val, { settings }) {
                        if (
                            !val ||
                            !val.length ||
                            !settings ||
                            (!settings.minOptions && !settings.maxOptions)
                        ) {
                            return true;
                        }

                        const {
                            minOptions: min = 0,
                            maxOptions: max = 0
                        } = settings;

                        return !(
                            !Array.isArray(val) ||
                            (min && val.length < min) ||
                            (max && val.length > max)
                        );
                    }
                }
            }
        },
        sitesPolicy: { checked: value => !!value }
    },

    data() {
        return {
            defaultLabels: {
                name: 'בקשה להוספת משרה חדשה',
                subname: 'פרטי המשרה',
                title: 'כותרת',
                subtitle: 'כותרת משנה',
                description: 'דרישות התפקיד',
                featuredImage: 'לוגו חברה'
            },
            labels: {},
            questions: [],
            serverErrors: [],
            sitesPolicy: false,
            post: {
                boardId: this.boardId,
                content: [],
                featuredImage: null
            }
        };
    },

    watch: {
        post() {
            this.$emit('input', this.post);
        }
    },

    async created() {
        const { questions, labels } = await this.getBoard(this.boardId);

        this.questions = questions;
        this.labels = labels;
    },

    methods: {
        ...mapActions({
            getBoard: 'boards/show',
            storePost: 'posts/store'
        }),

        async save() {
            this.serverErrors = [];
            this.error = '';

            this.$v.$touch();

            if (this.$v.$invalid) {
                await this.$nextTick();

                this.scrollToError();

                return;
            }

            try {
                const formData = new FormData();
                this.post.content = [];

                const recaptchaToken = await this.$recaptcha.execute('addPost');

                this.post._token = recaptchaToken;

                for (const { answer, label, type } of this.questions) {
                    if (answer || type === 'checkbox') {
                        if (type === 'file') {
                            formData.append(label, answer);
                        }

                        this.post.content.push({ answer, label, type });
                    }
                }

                for (const [key, value] of Object.entries(this.post)) {
                    if (key === 'content') {
                        formData.append(key, JSON.stringify(value));

                        continue;
                    }

                    formData.append(key, value);
                }

                const post = await this.storePost(formData);

                this.$toasted.success('הפוסט נוצר!');

                this.$emit('on-save', post);
            } catch (err) {
                const { response } = err;

                if (!response) {
                    console.error(err);
                    this.error = 'משהו השתבש';

                    return;
                }

                const { data, status } = response;

                if (status === HTTP.FORBIDDEN) {
                    this.$toasted.error('אינך מורשה להוסיף פוסט ללוח זה');

                    return;
                }

                if (status === HTTP.BAD_REQUEST && data.errors) {
                    this.serverErrors = data.errors;

                    this.$toasted.error('אנא בדקו את הערכים בטופס');

                    return;
                }

                if (status === HTTP.UNAUTHORIZED) {
                    this.error = 'Only available for logged in users';

                    return;
                }

                if (data) {
                    this.error = data;

                    return;
                }

                this.error = 'משהו השתבש';

                console.error(err);

                this.$sentry.captureException(err);
            }
        },

        onInput(answer, index) {
            const question = this.questions[index];

            this.clearServerError(question.label);

            this.questions.splice(index, 1, { ...question, answer });
        },

        changeFile() {
            if (!this.$refs.fileInput.files.length) {
                this.removeFile();

                return;
            }

            const [file] = this.$refs.fileInput.files;

            this.filename = file.name;

            this.post.featuredImage = file;
        },

        hasError(key) {
            return (
                this.$v.post[key].$error ||
                this.serverErrors.find(error => error.param === key)
            );
        },

        getError(key) {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.post,
                this.serverErrors,
                key
            );
        },

        clearServerError(label) {
            this.serverErrors = this.serverErrors.filter(
                error => error.param !== label
            );
        },

        scrollToError() {
            const addPostModal = document.getElementById('add_post');
            const [element] = this.$refs.form.getElementsByClassName(
                'is-invalid'
            );

            if (!element || !addPostModal) {
                return;
            }

            addPostModal.scrollTo({
                top: element.offsetTop,
                behavior: 'smooth'
            });
        },

        removeFile() {
            this.post.featuredImage = null;
            this.filename = '';
        }
    }
};
</script>
